import styled, { useTheme } from 'styled-components'

import { FlexColumn, FlexRow } from '@/@bothrs-ui/web/components/layout'
import { Span } from '@/@bothrs-ui/web/components/typography/Body'

import type { CMSTranslationDto, Doctor } from '../../booking/types/booking.type'
import type { ESignImage } from '../../types/e-sign.types'

/**
 * The subset of doctor fields this component renders. Kept structural so both a
 * booking `Doctor` and an `ESignDoctor` satisfy it without a mapping step.
 */
type DoctorInfoDoctor = {
  name: string
  images?: Partial<ESignImage>
  fieldOfSpecialisations?: Doctor['fieldOfSpecialisations']
  conventionStatus?: CMSTranslationDto
}

type DoctorInfoProps = {
  doctor: DoctorInfoDoctor
  description?: string
  size?: 'small' | 'large'
  showFieldOfSpecialisations?: boolean
}

const pickImageSrc = (images: DoctorInfoDoctor['images'], isLarge: boolean) => {
  const dpis = isLarge ? (['2x', '1x'] as const) : (['1x', '2x'] as const)

  for (const dpi of dpis) {
    const src = images?.['480px']?.[dpi] || images?.['479px']?.[dpi] || images?.['768px']?.[dpi]
    if (src) {
      return src
    }
  }

  return images?.original || ''
}

const DoctorInfo = ({ doctor, description, size = 'small', showFieldOfSpecialisations = false }: DoctorInfoProps) => {
  const theme = useTheme()
  const isLarge = size === 'large'

  // eSign only reliably delivers the 479px sizes and the original; fall back
  // through the available sizes so a photo is shown whenever one exists. The
  // large avatar prefers 2x so it stays sharp.
  const imageSrc = pickImageSrc(doctor.images, isLarge)

  return (
    <FlexRow gap={theme.UI.SpacingPx.Space2} alignItems='center'>
      <StImageContainer size={size}>
        {imageSrc ? (
          <StImagePicture
            src={imageSrc}
            alt={doctor.name}
            onError={({ currentTarget }) => {
              currentTarget.src = '/img/svg/doctor-male.svg'
            }}
          />
        ) : (
          <img src='/img/svg/doctor-male.svg' alt='' />
        )}
      </StImageContainer>
      <FlexColumn>
        <Span type={isLarge ? 'BodyLargeBold' : 'BodyMediumRegular'}>{doctor.name}</Span>
        {description ? (
          <Span type='BodyMediumRegular' color={theme.Colors.Neutrals.Darker}>
            {description}
          </Span>
        ) : null}
        {showFieldOfSpecialisations &&
        doctor.fieldOfSpecialisations &&
        doctor.fieldOfSpecialisations.length > 0 &&
        doctor.fieldOfSpecialisations.length <= 3 ? (
          <Span type='BodySmallRegular' color={theme.Colors.Neutrals.Darker}>
            {doctor.fieldOfSpecialisations.map((field) => field.display || field.name).join(', ')}
          </Span>
        ) : null}
        {doctor.conventionStatus?.nl ? (
          <Span type='BodySmallRegular' color={theme.Colors.Neutrals.Darker}>
            {doctor.conventionStatus.nl}
          </Span>
        ) : null}
      </FlexColumn>
    </FlexRow>
  )
}

export { DoctorInfo }

/* ------------------------------------------------------------------------------------------------- *
 * Styled
 * ------------------------------------------------------------------------------------------------- */

const StImagePicture = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  image-rendering: auto;
`

const StImageContainer = styled.div<{ size: 'small' | 'large' }>`
  ${({ theme, size }) => `
    display: flex;
    justify-content: center;
    align-items: center;
    overflow: hidden;
    border-radius: ${theme.UI.BorderRadius.Small}px;
    width: ${size === 'small' ? '40px' : '60px'};
    height: ${size === 'small' ? '40px' : '60px'};
    background-color: ${theme.Colors.Neutrals.Lighter};
  `}
`
